import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, ChevronDown } from "lucide-react";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export function ChangePasswordCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  if (user?.view_only) return null;
  const mismatch = f.confirm && f.new_password !== f.confirm;
  async function save(e) {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      if (data.token) localStorage.setItem("sparkora_token", data.token);
      toast.success("Password changed. Other devices have been signed out.");
      setF(EMPTY); setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  const field = (k, label, testid) => (
    <div><Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={show ? "text" : "password"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required minLength={k === "current_password" ? 1 : 6} className="mt-1" autoComplete={k === "current_password" ? "current-password" : "new-password"} /></div>
  );
  return (
    <div className="bg-white rounded-2xl shadow-sm max-w-xl mt-5 overflow-hidden">
      <button type="button" data-testid="profile-change-password-toggle" onClick={() => setOpen(!open)} className="w-full px-4 py-3 text-sm font-semibold text-slate-800 flex items-center gap-2 hover:bg-slate-50 transition-colors">
        <KeyRound size={16} /> Change password <ChevronDown size={16} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <form onSubmit={save} data-testid="change-password-form" className="px-4 pb-4 space-y-3">
            {field("current_password", "Current password", "cp-current")}
            {field("new_password", "New password (min 6 characters)", "cp-new")}
            {field("confirm", "Confirm new password", "cp-confirm")}
            {mismatch && <p data-testid="cp-mismatch" className="text-xs text-rose-600">Passwords do not match</p>}
            <div className="flex items-center gap-2">
              <Button data-testid="cp-save" type="submit" disabled={busy || mismatch} className="btn-brand">{busy && <Loader2 size={14} className="animate-spin mr-1" />}Update password</Button>
              <Button type="button" variant="ghost" size="sm" data-testid="cp-show" onClick={() => setShow(!show)}>{show ? <EyeOff size={15} /> : <Eye size={15} />}<span className="ml-1">{show ? "Hide" : "Show"}</span></Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
